import React, { useState } from 'react';
import classnames from 'classnames';
import { useAsyncEffect, useDeepCompareEffect } from 'ahooks';
import { Layout } from 'tea-component';
import EllipsisHover from '@home/components/EllipsisHover';
import Loading from '@gp/components/Loading';
import PageAccess from '@gp/components/PageAccess';
import GeneralIFrame from '@gp/components/GeneralIFrame';
import NavigatorToolbar from './components/NavigatorToolbar';
import useRequest from '@gp/hooks/useRequest';
import useCOS from '@gp/hooks/useCos/useCOS';
import { isFilePreviewableByCOSWithSuffixAddVideo, isVideoWithSuffix, isImageWithSuffix , getFileInfoByUrl, isSafari, isFilePreviewableByCOSWithSuffix } from '@gp/utils/common';
import type { IBaseProps } from '@home/types/common';
import './index.scss';

interface ICOSDocPreviewerProps extends IBaseProps {
  // 页头标题
  title?: string | React.ReactElement | null;
  // 头部工具栏
  toolbar?: string | React.ReactElement | null;
  // 预览文档在cos的存储key
  docKey: string;
  // 预览文档文件名
  docName: string;
  // 预览文档格式
  docSuffix: string;
  // 获取是否有权限访问该文档预览页面的入参
  getAuthParams?: any;
  // 获取是否有权限访问该文档预览页面
  getAuth: (params?: any) => Promise<boolean>;
  // 无权限访问该文档预览页时展示的UI
  noAuth: string | React.ReactElement | null;
  // cos配置
  cosOptions: IUseCOSOptions;
}
const getTitle = (
  title?: string | React.ReactElement | null,
  name?: string,
  suffix?: string,
): string | React.ReactElement | null => {
  if (title) return title;
  if (!name) return '';
  if (!suffix) return name;
  return `${name}.${suffix}`;
};

const COSDocPreviewer: React.FC<ICOSDocPreviewerProps> = (props) => {
  const { style, className, title, toolbar, docKey, docName, docSuffix, getAuthParams, getAuth, noAuth, cosOptions } =
    props;

  const hasToolbar = React.isValidElement(toolbar);

  const { loading: authLoading, data: authData, run: authRun } = useRequest(getAuth, { manual: true });

  const { isCOSInited, isObjectExist, getDocumentPreviewUrl, getObjectUrl, getImagePreviewUrl } = useCOS(cosOptions);

  // 是否文档链接加载中
  const [docPreviewLoading, setDocPreviewLoading] = useState(true);

  // 是否文档格式错误
  const [docFormatError, setDocFormatterError] = useState(false);

  // 是否文档不存在
  const [docExistError, setDocExistError] = useState(false);

  // 是否文档为图片
  const [docIsImage, setDocIsImage] = useState(false);

  // 文档预览url
  const [docPreviewUrl, setDocPreviewUrl] = useState('');

  // 是否权限数据、文档链接加载中
  const loading = authLoading || docPreviewLoading;

  // 检查文档是否存在
  const checkExist = async (): Promise<boolean> => {
    const exist = await isObjectExist({ key: docKey });
    return exist;
  };

  // 获取文档预览链接
  const initPreview = async () => {
    setDocPreviewLoading(true);
    let url = '';
    if (isVideoWithSuffix(docSuffix)) {
      const res = await getObjectUrl({
        key: docKey || '',
      });
      url = res?.url || '';
    } else if (isImageWithSuffix(docSuffix)) {
      setDocIsImage(true);
      const res = await getImagePreviewUrl({
        key: docKey || '',
      });
      url = res?.url || '';
    } else {
      const res = await getDocumentPreviewUrl({ key: docKey });
      url = res?.url || '';
    }
    setDocPreviewLoading(false);
    setDocFormatterError(false);
    setDocExistError(false);
    setDocPreviewUrl(url);
  };

  // 第一步加载权限数据
  useDeepCompareEffect(() => {
    authRun(getAuthParams);
  }, [getAuthParams]);

  /**
   * 第二步获取文档预览链接
   * 1、无权限时，不获取文档预览链接，直接展示无权限交互
   * 2、文档格式错误时，不获取文档预览链接，直接展示文档格式错误交互
   * 3、cos未初始化完成时，不继续获取文档预览链接
   */
  useAsyncEffect(async () => {
    if (authLoading) return;

    // 没权限
    if (!authData) {
      setDocPreviewLoading(false);
      setDocFormatterError(false);
      setDocExistError(false);
      setDocPreviewUrl('');
      return;
    }
    // 文档格式错误
    if (!isFilePreviewableByCOSWithSuffixAddVideo(docSuffix)) {
      setDocPreviewLoading(false);
      setDocFormatterError(true);
      setDocExistError(false);
      setDocPreviewUrl('');
      return;
    }
    // COS未初始化完成
    if (!isCOSInited) return;

    const exist = await checkExist();

    // 文档不存在
    if (!exist) {
      setDocPreviewLoading(false);
      setDocFormatterError(false);
      setDocExistError(true);
      setDocPreviewUrl('');
      return;
    }
    // 获取文档预览链接，初始化文档预览内容
    initPreview();
  }, [authLoading, authData, isCOSInited, docKey, docSuffix]);

  return (
    <Layout style={style} className={classnames('dbg-cos-doc-previewer', className, { 'has-toolbar': hasToolbar })}>
      <Layout.Header>
        <EllipsisHover text={loading ? '' : getTitle(title, docName, docSuffix)} />
      </Layout.Header>
      <Layout.Body>
        <Layout.Content>
          {/* 加载中 */}
          {loading && <Loading loading innerStyle={{ color: '#fff' }} />}
          {/* 加载完成，并且无权限 */}
          {!loading && !authData && noAuth}
          {/* 加载完成，并且有权限，并且文档格式异常 */}
          {!loading && authData && docFormatError && (
            <PageAccess isAccess={false} description="The document is not available for preview at this time.">
              <span></span>
            </PageAccess>
          )}
          {/* 加载完成，并且有权限，并且文档不存在 */}
          {!loading && authData && !docFormatError && docExistError && (
            <PageAccess isAccess={false} description="The document has been deleted by the user.">
              <span></span>
            </PageAccess>
          )}
          {/* 加载完成，并且有权限，并且文档格式可预览 */}

          {!loading && authData && !docFormatError && !docExistError && (
            <>
              {docIsImage ? <img src={docPreviewUrl} alt={docName} /> : (<>
                {hasToolbar && <div className="dbg-cos-doc-previewer__toolbar">{toolbar}</div>}
                <div className="dbg-cos-doc-previewer__context">
                  <GeneralIFrame src={docPreviewUrl} docSuffix={docSuffix} />
                </div>
              </>)}
            </>
          )}
        </Layout.Content>
      </Layout.Body>
    </Layout>
  );
};

export { NavigatorToolbar };

export default COSDocPreviewer;
